import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Doodles";
import { Pin, PinCard } from "@/components/PinCard";
import { PinReadiness } from "@/components/PinReadiness";

const STATUSES = [["scheduled", "To post"], ["posted", "Posted"], ["", "All"]] as const;
const nyToday = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
const nice = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", ...(d.slice(0, 4) !== nyToday().slice(0, 4) ? { year: "numeric" } : {}) });

export default function PinsPage() {
  const [pins, setPins] = useState<Pin[] | null>(null);
  const [sections, setSections] = useState<Record<string, string>>({});
  const [status, setStatus] = useState("scheduled");
  const [section, setSection] = useState("");
  const load = useCallback(() => {
    api.get("/admin/pins", { params: { status, section } }).then((r) => { setPins(r.data.pins); setSections(r.data.sections); }).catch((e) => toast.error(formatApiError(e)));
  }, [status, section]);
  useEffect(() => { load(); }, [load]);
  const groups = useMemo(() => {
    const g: Record<string, Pin[]> = {};
    (pins || []).forEach((p) => { (g[p.scheduled_for] ||= []).push(p); });
    return Object.entries(g);
  }, [pins]);
  const today = nyToday();
  return (
    <div data-testid="pins-page">
      <PageHeader title="Pins" kicker="Download, copy the text, post on Pinterest, then mark it posted" />
      <PinReadiness onChanged={load} />
      <div className="mb-4 rounded-2xl border-2 border-dashed border-[#23324A]/25 bg-[#FFF8E5] p-4 text-sm text-[#23324A]" data-testid="pins-howto">
        Pinterest isn't connected yet, so nothing posts by itself. For each pin: <b>1.</b> click <b>Image</b> to download it, <b>2.</b> on Pinterest choose Create Pin and upload it, <b>3.</b> click <b>Text</b> and paste the title, description and link, <b>4.</b> come back and click <b>Mark as posted</b>.
      </div>
      <div className="mb-6 flex flex-wrap items-center gap-2">
        {STATUSES.map(([k, l]) => <button key={k} className={`chip ${status === k ? "chip-active" : ""}`} onClick={() => setStatus(k)} data-testid={`pins-status-${k || "all"}`}>{l}</button>)}
        <select className="rounded-full border-2 border-[#23324A]/30 bg-white px-3 py-2 text-sm" value={section} onChange={(e) => setSection(e.target.value)} data-testid="pins-section-filter">
          <option value="">All sections</option>
          {Object.entries(sections).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {pins && <span className="text-xs text-[#23324A]/60" data-testid="pins-count">{pins.length} pin{pins.length === 1 ? "" : "s"}</span>}
      </div>
      {!pins && <p className="text-sm text-[#23324A]/60">Loading…</p>}
      {pins && !pins.length && <p className="text-sm text-[#23324A]/60" data-testid="pins-empty">No pins here.</p>}
      <div className="space-y-8">
        {groups.map(([day, list]) => (
          <section key={day} data-testid={`pins-day-${day}`}>
            <h2 className="mb-3 font-display text-base font-extrabold text-[#23324A] md:text-lg">
              {nice(day)}{day < today && status === "scheduled" ? " · overdue" : day === today ? " · today" : ""}
            </h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
              {list.map((p) => <PinCard key={`${p.id}-${p.rendered_at}-${p.status}`} pin={p} onChange={load} />)}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
